<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardSubtitle from "@/components/shared/CardSubtitle.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import Icon from "@/components/shared/Icon.vue"
import InputBox from "@/components/shared/InputBox.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import router from "@/plugins/router"
import store from "@/plugins/store"
import qualityService from "@/services/quality"
import type { Partner } from "@/types/partner.type"
import type { QualityPriceTag } from "@/types/quality-price.type"
import type { QualityDetail } from "@/types/quality.type"
import { minimumUnit } from "@/utils/money"
import { reportSentry } from "@/utils/sentry"

const route = useRoute()
const { t } = useI18n()

const item = ref<QualityDetail | null>(null)
const partner = computed<Partner | null>(() => store.getters.partner)
const id = computed(() => route.params.id)
const pageTitle = computed(() => {
  return `${t("item.editPrices")} #${item.value?.name || ""}`
})
const isPrintOrderable = computed(() => item.value?.isPrintOrderable)
const currency = computed(() => {
  if (item.value?.standardOrderPriceTagGroup) return item.value.standardOrderPriceTagGroup.currency
  else if (item.value?.printPriceTagGroup) return item.value.printPriceTagGroup.currency
  else if (partner.value?.tradingCurrency) return partner.value.tradingCurrency
  else return "krw"
})

const standardSamplePrice = ref<QualityPriceTag | null>(null)
const standardWholesalePrices = ref<QualityPriceTag[]>([])

function addStandardSamplePrice() {
  standardSamplePrice.value = {
    range: "sample",
    quantityGteq: null,
    quantityLteq: null,
    price: null,
  } as QualityPriceTag
}

function removeStandardSamplePrice() {
  standardSamplePrice.value = null
}

function addStandardWholesalePrice() {
  standardWholesalePrices.value.push({
    range: "bulk",
    quantityGteq: null,
    quantityLteq: null,
    price: null,
  } as QualityPriceTag)
}

function removeStandardWholesalePrice(i: number) {
  standardWholesalePrices.value.splice(i, 1)
}

const printSamplePrice = ref<QualityPriceTag | null>(null)
const printWholesalePrices = ref<QualityPriceTag[]>([])

function addPrintSamplePrice() {
  printSamplePrice.value = {
    range: "sample",
    quantityGteq: null,
    quantityLteq: null,
    price: null,
  } as QualityPriceTag
}

function removePrintSamplePrice() {
  printSamplePrice.value = null
}

function addPrintWholesalePrice() {
  printWholesalePrices.value.push({
    range: "bulk",
    quantityGteq: null,
    quantityLteq: null,
    price: null,
  } as QualityPriceTag)
}

function removePrintWholesalePrice(i: number) {
  printWholesalePrices.value.splice(i, 1)
}

watchEffect(() => {
  const standardLength = standardWholesalePrices.value.length
  for (let i = 0; i < standardLength; i++) {
    const tag = standardWholesalePrices.value[i]!
    const tagNext = standardWholesalePrices.value[i + 1]
    if (tagNext && tagNext.quantityGteq) tag.quantityLteq = tagNext.quantityGteq - 1
    else tag.quantityLteq = null
  }

  const printLength = printWholesalePrices.value.length
  for (let i = 0; i < printLength; i++) {
    const tag = printWholesalePrices.value[i]!
    const tagNext = printWholesalePrices.value[i + 1]
    if (tagNext && tagNext.quantityGteq) tag.quantityLteq = tagNext.quantityGteq - 1
    else tag.quantityLteq = null
  }
})

async function loadItem() {
  item.value = await qualityService.get(Number(id.value))

  if (item.value.standardOrderPriceTagGroup) {
    standardSamplePrice.value =
      item.value.standardOrderPriceTagGroup.qualityPriceTags.find((e) => e.range === "sample") || null
    standardWholesalePrices.value = item.value.standardOrderPriceTagGroup.qualityPriceTags.filter(
      (e) => e.range === "bulk",
    )
  } else {
    standardSamplePrice.value = {
      range: "sample",
      quantityGteq: 1,
      quantityLteq: 49,
      price: null,
    } as QualityPriceTag
    standardWholesalePrices.value = [
      {
        range: "bulk",
        quantityGteq: 50,
        quantityLteq: 99,
        price: null,
      },
      {
        range: "bulk",
        quantityGteq: 100,
        quantityLteq: null,
        price: null,
      },
    ] as QualityPriceTag[]
  }

  if (item.value.printPriceTagGroup) {
    printSamplePrice.value = item.value.printPriceTagGroup.qualityPriceTags.find((e) => e.range === "sample") || null
    printWholesalePrices.value = item.value.printPriceTagGroup.qualityPriceTags.filter((e) => e.range === "bulk")
  } else {
    printSamplePrice.value = {
      range: "sample",
      quantityGteq: 1,
      quantityLteq: 20,
      price: null,
    } as QualityPriceTag
    printWholesalePrices.value = [
      {
        range: "bulk",
        quantityGteq: 50,
        quantityLteq: 99,
        price: null,
      },
      {
        range: "bulk",
        quantityGteq: 100,
        quantityLteq: null,
        price: null,
      },
    ] as QualityPriceTag[]
  }
}

onMounted(async () => {
  try {
    await loadItem()
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/items")
  }
})

const isSubmitting = ref(false)
const errorMessage = ref("")

function validateTags(tags: QualityPriceTag[]) {
  const errors = []

  let maxQuantityLteq = null as number | null
  for (const tag of tags) {
    if (tag.quantityLteq && tag.quantityGteq && tag.quantityLteq < tag.quantityGteq) {
      errors.push(t("item.errors.wrongAmountRange"))
      break
    }

    if (maxQuantityLteq !== null && tag.quantityGteq && tag.quantityGteq <= maxQuantityLteq) {
      errors.push(t("item.errors.wrongAmountRange"))
      break
    }
    maxQuantityLteq = tag.quantityLteq
  }

  return errors
}

function validate() {
  const errors = []

  if (standardWholesalePrices.value.length < 1 || (isPrintOrderable.value && printWholesalePrices.value.length < 1)) {
    errors.push(t("item.errors.emptyWholesalePricing"))
  }

  let tags = [...standardWholesalePrices.value]
  if (standardSamplePrice.value) {
    tags.unshift(standardSamplePrice.value)
  }
  errors.push(...validateTags(tags))

  if (isPrintOrderable.value) {
    const printTags = [...printWholesalePrices.value]
    if (printSamplePrice.value) {
      printTags.unshift(printSamplePrice.value)
    }

    errors.push(...validateTags(printTags))
  }

  if (errors.length > 0) {
    errorMessage.value = [...new Set(errors)].join("\n")
    return false
  } else {
    return true
  }
}

function makeStandardPayload() {
  const payload = {
    standardOrderPriceTagGroup: {
      salesType: "standard_order",
      printMethod: "none",
      qualityPriceTags: [] as QualityPriceTag[],
    },
  }
  const tags = [...standardWholesalePrices.value]
  if (standardSamplePrice.value) tags.unshift(standardSamplePrice.value)
  payload.standardOrderPriceTagGroup.qualityPriceTags = tags
  return payload
}

function makePrintPayload() {
  const payload = {
    printPriceTagGroup: {
      salesType: "print",
      printMethod: "none",
      qualityPriceTags: [] as QualityPriceTag[],
    },
  }
  const tags = [...printWholesalePrices.value]
  if (printSamplePrice.value) tags.unshift(printSamplePrice.value)
  payload.printPriceTagGroup.qualityPriceTags = tags
  return payload
}

async function saveItem() {
  isSubmitting.value = true
  errorMessage.value = ""

  if (!validate()) {
    isSubmitting.value = false
    return
  }

  try {
    await qualityService.updateStandardOrderPriceTags(Number(id.value), makeStandardPayload())

    if (isPrintOrderable.value) {
      await qualityService.updatePrintPriceTags(Number(id.value), makePrintPayload())
    }

    router.push(`/items/${id.value}`)
  } catch (err) {
    errorMessage.value = t("shared.errors.unknown")
    isSubmitting.value = false
  }
}
</script>

<template>
  <LayoutAuthed :title="pageTitle">
    <div class="row container">
      <div class="col-12 col-lg-9 col-xxl-7">
        <form @submit.prevent="saveItem()">
          <Card>
            <CardTitle v-if="item?.isPrintOrderable">{{ $t("item.priceSalesType.standardOrder") }}</CardTitle>
            <CardSubtitle>{{ $t("item.priceTag.rangeType.sample") }}</CardSubtitle>
            <CardContent>
              <div class="row" v-if="standardSamplePrice">
                <div class="col">
                  <InputBox
                    v-model="standardSamplePrice.quantityGteq"
                    type="number"
                    field="quantityGteq"
                    :suffix="$t('item.units.yards_short')"
                    :min="1"
                    required
                  />
                </div>
                <div class="col-auto d-flex align-items-center">
                  <span>-</span>
                </div>
                <div class="col">
                  <InputBox
                    v-model="standardSamplePrice.quantityLteq"
                    type="number"
                    field="quantityLteq"
                    :suffix="$t('item.units.yards_short')"
                    :min="1"
                    required
                  />
                </div>
                <div class="col-auto">
                  <!-- Empty column for spacing -->
                </div>
                <div class="col-4">
                  <InputBox
                    v-model="standardSamplePrice.price"
                    type="number"
                    field="price"
                    :suffix="$t(`item.units.${currency}`)"
                    :step="minimumUnit(currency)"
                    :min="minimumUnit(currency)"
                    required
                  />
                </div>
                <div class="col-auto d-flex align-items-center">
                  <Icon icon="square-xmark" class="text-danger font-larger" @click="removeStandardSamplePrice()" />
                </div>
              </div>
              <div v-else>
                {{ $t("item.guides.sampleNotAvailable") }}
                <a class="text-primary cursor-pointer" @click.prevent="addStandardSamplePrice()">
                  <u>Add Sample Price</u>
                </a>
              </div>
            </CardContent>
            <CardSubtitle>{{ $t("item.priceTag.rangeType.bulk") }}</CardSubtitle>
            <CardContent>
              <div class="row" v-for="(price, i) in standardWholesalePrices" :key="i" :class="{ 'mt-3': i > 0 }">
                <div class="col">
                  <InputBox
                    v-model="price.quantityGteq"
                    type="number"
                    field="quantityGteq"
                    :suffix="$t('item.units.yards_short')"
                    :min="1"
                    required
                  />
                </div>
                <div class="col-auto d-flex align-items-center">
                  <span>-</span>
                </div>
                <div class="col d-flex align-items-center">
                  <span v-if="i < standardWholesalePrices.length - 1">
                    {{ price.quantityLteq ?? "" }} {{ $t("item.units.yards_short") }}
                  </span>
                  <span v-if="i === standardWholesalePrices.length - 1">{{ $t("item.priceTag.quantityAndUp") }}</span>
                </div>
                <div class="col-auto">
                  <!-- Empty column for spacing -->
                </div>
                <div class="col-4">
                  <InputBox
                    v-model="price.price"
                    type="number"
                    field="price"
                    :suffix="$t(`item.units.${currency}`)"
                    :step="minimumUnit(currency)"
                    :min="minimumUnit(currency)"
                    required
                  />
                </div>
                <div class="col-auto d-flex align-items-center">
                  <Icon icon="square-xmark" class="text-danger font-larger" @click="removeStandardWholesalePrice(i)" />
                </div>
              </div>
              <Button text="+ Add Price" size="sm" class="mt-3" @click="addStandardWholesalePrice()" />
            </CardContent>

            <template v-if="item?.isPrintOrderable">
              <CardTitle>{{ $t("item.priceSalesType.print") }}</CardTitle>
              <CardSubtitle>{{ $t("item.priceTag.rangeType.sample") }}</CardSubtitle>
              <CardContent>
                <div class="row" v-if="printSamplePrice">
                  <div class="col">
                    <InputBox
                      v-model="printSamplePrice.quantityGteq"
                      type="number"
                      field="quantityGteq"
                      :suffix="$t('item.units.yards_short')"
                      :min="1"
                      required
                    />
                  </div>
                  <div class="col-auto d-flex align-items-center">
                    <span>-</span>
                  </div>
                  <div class="col">
                    <InputBox
                      v-model="printSamplePrice.quantityLteq"
                      type="number"
                      field="quantityLteq"
                      :suffix="$t('item.units.yards_short')"
                      :min="1"
                      required
                    />
                  </div>
                  <div class="col-auto">
                    <!-- Empty column for spacing -->
                  </div>
                  <div class="col-4">
                    <InputBox
                      v-model="printSamplePrice.price"
                      type="number"
                      field="price"
                      :suffix="$t(`item.units.${currency}`)"
                      :step="minimumUnit(currency)"
                      :min="minimumUnit(currency)"
                      required
                    />
                  </div>
                  <div class="col-auto d-flex align-items-center">
                    <Icon icon="square-xmark" class="text-danger font-larger" @click="removePrintSamplePrice()" />
                  </div>
                </div>
                <div v-else>
                  {{ $t("item.guides.sampleNotAvailable") }}
                  <a class="text-primary cursor-pointer" @click.prevent="addPrintSamplePrice()">
                    <u>Add Sample Price</u>
                  </a>
                </div>
              </CardContent>
              <CardSubtitle>{{ $t("item.priceTag.rangeType.bulk") }}</CardSubtitle>
              <CardContent>
                <div class="row" v-for="(price, i) in printWholesalePrices" :key="i" :class="{ 'mt-3': i > 0 }">
                  <div class="col">
                    <InputBox
                      v-model="price.quantityGteq"
                      type="number"
                      field="quantityGteq"
                      :suffix="$t('item.units.yards_short')"
                      :min="1"
                      required
                    />
                  </div>
                  <div class="col-auto d-flex align-items-center">
                    <span>-</span>
                  </div>
                  <div class="col d-flex align-items-center">
                    <span v-if="i < printWholesalePrices.length - 1"
                      >{{ price.quantityLteq ?? "" }} {{ $t("item.units.yards_short") }}
                    </span>
                    <span v-if="i === printWholesalePrices.length - 1">{{ $t("item.priceTag.quantityAndUp") }}</span>
                  </div>
                  <div class="col-auto">
                    <!-- Empty column for spacing -->
                  </div>
                  <div class="col-4">
                    <InputBox
                      v-model="price.price"
                      type="number"
                      field="price"
                      :suffix="$t(`item.units.${currency}`)"
                      :step="minimumUnit(currency)"
                      :min="minimumUnit(currency)"
                      required
                    />
                  </div>
                  <div class="col-auto d-flex align-items-center">
                    <Icon icon="square-xmark" class="text-danger font-larger" @click="removePrintWholesalePrice(i)" />
                  </div>
                </div>
                <Button text="+ Add Price" size="sm" class="mt-3" @click="addPrintWholesalePrice()" />
              </CardContent>
            </template>

            <CardActions displayBlock>
              <Button type="submit" color="dark" :text="$t('shared.save')" :isSubmitting="isSubmitting" />
              <span class="text-center text-danger error-message">{{ errorMessage }}</span>
            </CardActions>
          </Card>
        </form>
      </div>
    </div>
  </LayoutAuthed>
</template>

<style scoped>
@media (max-width: 991.98px) {
  .container {
    min-width: 620px;
  }
}

.error-message {
  font-size: 16px;
  font-weight: bold;
  white-space: pre-line;
}
</style>
